import { CARD_KEYS, DATE_PERIODS, MAX_RANGE_DAYS, NO_ACCESS_MESSAGE } from '../constants/userAnalytics';
import { formatEtDate, formatEtRange } from './formatEt';
import { extractErrorMessage } from './errorMessage';

// Pure helpers shared by the cards, the modal and the exports, so the
// same number is never formatted two different ways.

const LOCALE = 'en-US';

export const fmtCount = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString(LOCALE) : '—');

const round1 = (n) => Math.round(n * 10) / 10;

// A delta badge must never print "+0%", "NaN%" or "+-5.2%": null means "no prior data".
export const deltaView = (deltaPct) => {
  if (typeof deltaPct !== 'number' || !Number.isFinite(deltaPct)) return { text: '—', tone: 'none' };
  const r = round1(deltaPct);
  if (r === 0) return { text: '0.0%', tone: 'flat' };
  return r > 0 ? { text: `+${r.toFixed(1)}%`, tone: 'up' } : { text: `${r.toFixed(1)}%`, tone: 'down' };
};

export const cardValue = (summary, key) => {
  if (!summary) return null;
  switch (key) {
    case CARD_KEYS.TOTAL: return summary.totals?.total ?? null;
    case CARD_KEYS.IMMERSIVE: return summary.totals?.immersive ?? null;
    case CARD_KEYS.CMS: return summary.totals?.cms ?? null;
    case CARD_KEYS.DAU: return summary.active?.dau?.value ?? null;
    case CARD_KEYS.WAU: return summary.active?.wau?.value ?? null;
    case CARD_KEYS.MAU: return summary.active?.mau?.value ?? null;
    default: return null;
  }
};

// "@Globalmobisoft.COM " -> "globalmobisoft.com". Partial text stays valid while typing.
export const normalizeDomain = (raw) => String(raw ?? '').trim().toLowerCase().replace(/^@/, '').trim();

const dayNumber = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
};

/** Error text for an invalid From/To pair (ET calendar dates), or null when it can be applied. */
export const validateDateRange = (from, to, today) => {
  if (!from || !to) return 'Choose both dates.';
  if (from > to) return 'Start date must be on or before the end date.';
  if (to > today) return 'End date cannot be in the future.';
  if (dayNumber(to) - dayNumber(from) + 1 > MAX_RANGE_DAYS) return `Range cannot exceed ${MAX_RANGE_DAYS} days.`;
  return null;
};

const periodBadge = (period) => DATE_PERIODS.find((p) => p.value === period)?.badge ?? 'Custom';

/** The range the server resolved for the data on screen, only if it is for the requested period. */
export const servedRange = (requested, served) => {
  if (!served?.from || !served?.to) return null;
  if (String(served.period ?? '').toLowerCase() !== requested.period) return null;
  if (requested.period === 'custom' && (served.from !== requested.from || served.to !== requested.to)) return null;
  return { from: served.from, to: served.to };
};

/** "Last 30 Days (Sep 15, 2026 – Oct 14, 2026)" — label only until a range is known. */
export const dateBadgeText = (period, range) => {
  const label = periodBadge(period);
  if (!range) return label;
  if (period === 'today') return `${label} (${formatEtDate(range.to)})`;
  return `${label} (${formatEtRange(range.from, range.to)})`;
};

/**
 * Entity / space / domain text for the badge strip, the popup subtitle and the exports.
 * `restricted` is the marketing-manager view: no entity, no domain, just the open space.
 */
export const buildFilterLabels = ({ filters, entities = [], restricted = false, spaceName = '', domain = '' }) => {
  if (restricted) return { entity: null, space: spaceName || null, domain: null };
  const entity = entities.find((e) => e.id === filters.entityId);
  const space = entities.flatMap((e) => e.spaces ?? []).find((s) => s.id === filters.spaceId);
  return {
    entity: filters.entityId == null ? 'All Entities' : (entity?.name ?? `Entity ${filters.entityId}`),
    space: filters.spaceId == null ? 'All Spaces' : (space?.name ?? `Space ${filters.spaceId}`),
    domain: domain || null,
  };
};

export const modalSubtext = (labels, dateBadge, { restricted = false } = {}) =>
  [
    labels.entity,
    labels.space && (restricted ? `Space: ${labels.space}` : labels.space),
    labels.domain && `Domain: @${labels.domain}`,
    dateBadge,
  ].filter(Boolean).join(' • ');

export const describeAnalyticsError = (err) => {
  const status = err?.response?.status;
  if (status === 403) return { kind: 'forbidden', message: NO_ACCESS_MESSAGE };
  if (status === 400) return { kind: 'validation', message: extractErrorMessage(err, 'The selected filters are not valid.') };
  return { kind: 'generic', message: '' };
};
